// src/components/pdf/charts/AdvancedCharts.jsx
import React from 'react';
import { View, Text, Svg, Path, Rect } from '@react-pdf/renderer';

// Arrondi intelligent (largest remainder method) pour que le total fasse exactement 100%
const roundPercentages = (segments) => {
  const withFloor = segments.map(seg => ({
    ...seg,
    pct: Math.floor(seg.percentage * 100),
    remainder: (seg.percentage * 100) % 1,
  }));
  let remaining = 100 - withFloor.reduce((s, seg) => s + seg.pct, 0);
  return withFloor
    .sort((a, b) => b.remainder - a.remainder)
    .map((seg, i) => ({ ...seg, pct: seg.pct + (i < remaining ? 1 : 0) }))
    .sort((a, b) => segments.findIndex(s => s.name === a.name) - segments.findIndex(s => s.name === b.name));
};

/**
 * Composant PieChart simple pour react-pdf (format Donut)
 */
export const SimplePieChart = ({ data, width = 200, height = 200, title, subtitle, subtitle2 }) => {
  // SVG élargi avec marges asymétriques : plus de place à gauche pour les labels longs
  const svgWidth = width + 80;
  const svgHeight = height + 40;
  const centerX = svgWidth / 2;
  const centerY = svgHeight / 2;
  const outerRadius = 68;
  const innerRadius = 52;
  
  // Calculer le total
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return null;
  
  // Générer les segments
  let currentAngle = -90; // Commencer en haut
  const rawSegments = data.map(item => {
    const percentage = item.value / total;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    return { ...item, startAngle, endAngle, percentage };
  });
  // Appliquer l'arrondi intelligent pour que le total fasse 100%
  const segments = roundPercentages(rawSegments);
  
  // Fonction pour créer un path d'arc (donut)
  const createDonutPath = (startAngle, endAngle, outerRadius, innerRadius, cx, cy) => {
    const startOuter = polarToCartesian(cx, cy, outerRadius, endAngle);
    const endOuter = polarToCartesian(cx, cy, outerRadius, startAngle);
    const startInner = polarToCartesian(cx, cy, innerRadius, endAngle);
    const endInner = polarToCartesian(cx, cy, innerRadius, startAngle);
    
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    
    return [
      `M ${startOuter.x} ${startOuter.y}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${endOuter.x} ${endOuter.y}`,
      `L ${endInner.x} ${endInner.y}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${startInner.x} ${startInner.y}`,
      'Z'
    ].join(' ');
  };
  
  const polarToCartesian = (cx, cy, radius, angle) => {
    const rad = (angle - 90) * Math.PI / 180;
    return {
      x: cx + radius * Math.cos(rad),
      y: cy + radius * Math.sin(rad)
    };
  };
  
  // Calculer la position des labels (plus proche du donut)
  const getLabelPosition = (startAngle, endAngle, radius, cx, cy) => {
    const midAngle = (startAngle + endAngle) / 2;
    const labelRadius = radius + 12;
    return polarToCartesian(cx, cy, labelRadius, midAngle);
  };
  
  // Vérifier si le label est long (doit être sur 2 lignes)
  const isLongLabel = (name) => {
    return name === 'Erreurs Adversaire' || 
           name === 'Points Adversaire' || 
           name.startsWith('Fautes ');
  };
  
  return (
    <View style={{ alignItems: 'center', marginBottom: 10 }}>
      {title && (
        <View style={{ alignItems: 'center', marginBottom: 8 }}>
          <Text style={{ fontSize: 10, fontWeight: 'bold', textAlign: 'center' }}>
            {title}
          </Text>
          {subtitle && (
            <Text style={{ fontSize: 7, color: '#666', textAlign: 'center', marginTop: 2 }}>
              {subtitle}
            </Text>
          )}
          {subtitle2 && (
            <Text style={{ fontSize: 7, color: '#666', textAlign: 'center', marginTop: 1 }}>
              {subtitle2}
            </Text>
          )}
        </View>
      )}
      
      <Svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`}>
        {/* Dessiner les segments du donut */}
        {segments.map((segment, index) => {
          const isFaute = segment.name.startsWith('Fautes');
          
          return (
            <Path
              key={index}
              d={createDonutPath(segment.startAngle, segment.endAngle, outerRadius, innerRadius, centerX, centerY)}
              fill={segment.color}
              opacity={isFaute ? "0.5" : "1"}
              stroke="none"
            />
          );
        })}
        
        {/* Labels avec pourcentages */}
        {segments.map((segment, index) => {
          const pos = getLabelPosition(segment.startAngle, segment.endAngle, outerRadius, centerX, centerY);
          const textAnchor = pos.x > centerX ? 'start' : 'end';
          const percentage = `${segment.pct ?? Math.round(segment.percentage * 100)}%`;
          
          // Si label long, mettre sur 2 lignes
          if (isLongLabel(segment.name)) {
            // Calculer l'offset pour centrer le pourcentage
            const textLength = segment.name.length * 4.5; // Estimation largeur
            const centerOffset = textAnchor === 'start' ? textLength / 2 : -textLength / 2;
            
            return (
              <React.Fragment key={`label-${index}`}>
                <Text
                  x={pos.x}
                  y={pos.y - 6}
                  style={{
                    fontSize: 7,
                    fill: segment.color,
                    textAnchor: textAnchor
                  }}
                >
                  {segment.name}
                </Text>
                <Text
                  x={pos.x + centerOffset}
                  y={pos.y + 6}
                  style={{
                    fontSize: 8,
                    fill: segment.color,
                    textAnchor: 'middle'
                  }}
                >
                  {percentage}
                </Text>
              </React.Fragment>
            );
          }
          
          // Sinon, label sur 1 ligne
          return (
            <Text
              key={`label-${index}`}
              x={pos.x}
              y={pos.y}
              style={{
                fontSize: 7,
                fill: segment.color,
                textAnchor: textAnchor
              }}
            >
              {`${segment.name} ${percentage}`}
            </Text>
          );
        })}
      </Svg>
    </View>
  );
};

/**
 * Composant BarChart vertical avec hachures (comme l'app mobile)
 */
export const VerticalBarChart = ({ data, title, color, showPoints = true, showFautes = true }) => {
  if (!data || data.length === 0) {
    return (
      <View style={{ marginBottom: 15 }}>
        <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, textAlign: 'center' }}>
          {title}
        </Text>
        <Text style={{ fontSize: 8, color: '#666', textAlign: 'center' }}>Aucune donnée</Text>
      </View>
    );
  }
  
  const chartHeight = 180;
  const topMargin = 20; // Marge pour les valeurs au-dessus des barres
  const chartWidth = 280; // Augmenté pour utiliser toute la largeur
  const numZones = data.length;
  const zoneWidth = chartWidth / numZones; // Largeur totale par zone
  const barWidth = Math.min(22, zoneWidth / 2.5); // Largeur d'une barre (2 barres par zone)
  const maxValue = Math.max(...data.map(d => Math.max(d.points || 0, d.fautes || 0)), 1);
  
  return (
    <View style={{ marginBottom: 15 }}>
      <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 6, textAlign: 'center' }}>
        {title}
      </Text>
      
      <Svg width={chartWidth} height={chartHeight + topMargin + 40} viewBox={`0 0 ${chartWidth} ${chartHeight + topMargin + 40}`}>
        {data.map((item, index) => {
          const zoneCenterX = (index + 0.5) * zoneWidth; // Centre de la zone
          const pointsHeight = maxValue > 0 ? (item.points / maxValue) * chartHeight : 0;
          const fautesHeight = maxValue > 0 ? (item.fautes / maxValue) * chartHeight : 0;
          
          return (
            <React.Fragment key={index}>
              {/* Barre points (solide) - à GAUCHE du centre */}
              {showPoints && item.points > 0 && (
                <>
                  <Rect
                    x={zoneCenterX - barWidth}
                    y={topMargin + chartHeight - pointsHeight}
                    width={barWidth}
                    height={pointsHeight}
                    fill={color}
                  />
                  {/* Valeur au-dessus */}
                  <Text
                    x={zoneCenterX - barWidth / 2}
                    y={topMargin + chartHeight - pointsHeight - 3}
                    style={{ fontSize: 8, textAnchor: 'middle' }}
                  >
                    {item.points}
                  </Text>
                </>
              )}
              
              {/* Barre fautes (avec opacité) - à DROITE du centre, ACCOLÉE */}
              {showFautes && item.fautes > 0 && (
                <>
                  {/* Barre avec opacité réduite */}
                  <Rect
                    x={zoneCenterX}
                    y={topMargin + chartHeight - fautesHeight}
                    width={barWidth}
                    height={fautesHeight}
                    fill={color}
                    opacity="0.5"
                  />
                  {/* Valeur au-dessus */}
                  <Text
                    x={zoneCenterX + barWidth / 2}
                    y={topMargin + chartHeight - fautesHeight - 3}
                    style={{ fontSize: 8, textAnchor: 'middle' }}
                  >
                    {item.fautes}
                  </Text>
                </>
              )}
              
              {/* Label zone - gérer "Passing/Lob" sur 2 lignes */}
              {item.label === 'Passing/Lob' ? (
                <>
                  <Text
                    x={zoneCenterX}
                    y={topMargin + chartHeight + 10}
                    style={{ fontSize: 7, textAnchor: 'middle' }}
                  >
                    Passing/
                  </Text>
                  <Text
                    x={zoneCenterX}
                    y={topMargin + chartHeight + 20}
                    style={{ fontSize: 7, textAnchor: 'middle' }}
                  >
                    Lob
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    x={zoneCenterX}
                    y={topMargin + chartHeight + 10}
                    style={{ fontSize: 7, textAnchor: 'middle' }}
                  >
                    {item.label.split(' ')[0]}
                  </Text>
                  {item.label.split(' ').length > 1 && (
                    <Text
                      x={zoneCenterX}
                      y={topMargin + chartHeight + 20}
                      style={{ fontSize: 7, textAnchor: 'middle' }}
                    >
                      {item.label.split(' ').slice(1).join(' ')}
                    </Text>
                  )}
                </>
              )}
            </React.Fragment>
          );
        })}
      </Svg>
      
      {/* Légende */}
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 15, marginTop: 0 }}>
        {showPoints && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 12, backgroundColor: color }} />
            <Text style={{ fontSize: 7 }}>Points gagnés</Text>
          </View>
        )}
        {showFautes && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 12, backgroundColor: color, opacity: 0.5 }} />
            <Text style={{ fontSize: 7 }}>Points perdus</Text>
          </View>
        )}
      </View>
    </View>
  );
};

/**
 * Composant BarChart horizontal simple pour react-pdf
 */
export const SimpleBarChart = ({ data, title, maxValue }) => {
  if (!data || data.length === 0) return null;
  
  const barHeight = 20;
  const chartWidth = 300;
  const labelWidth = 100;
  const maxBarWidth = chartWidth - labelWidth - 40;
  
  // Trouver la valeur max
  const max = maxValue || Math.max(...data.map(d => d.value));
  
  return (
    <View style={{ marginBottom: 15 }}>
      {title && (
        <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 8, textAlign: 'center' }}>
          {title}
        </Text>
      )}
      
      {data.map((item, index) => {
        const barWidth = max > 0 ? (item.value / max) * maxBarWidth : 0;
        
        return (
          <View key={index} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
            {/* Label */}
            <Text style={{ fontSize: 8, width: labelWidth, textAlign: 'right', paddingRight: 8 }}>
              {item.name}
            </Text>
            
            {/* Barre */}
            <View style={{ flex: 1 }}>
              <Svg width={maxBarWidth + 20} height={barHeight}>
                <Rect
                  x="0"
                  y="0"
                  width={barWidth}
                  height={barHeight}
                  fill={item.color || '#1976D2'}
                />
              </Svg>
            </View>
            
            {/* Valeur */}
            <Text style={{ fontSize: 8, marginLeft: 8, minWidth: 30 }}>
              {item.value}
            </Text>
          </View>
        );
      })}
    </View>
  );
};

/**
 * Composant pour afficher les commentaires
 */
export const CommentBox = ({ comment }) => {
  if (!comment || comment.trim() === '') return null;
  
  return (
    <View style={{
      backgroundColor: '#FFF9E6',
      padding: 10,
      borderRadius: 4,
      border: '1px solid #FFD700',
      marginTop: 10
    }}>
      <View style={{ flexDirection: 'row' }}>
        <Text style={{ fontSize: 9, fontWeight: 'bold', color: '#B8860B' }}>
          Commentaire :{' '}
        </Text>
        <Text style={{ fontSize: 8, color: '#333', lineHeight: 1.4, flex: 1 }}>
          {comment}
        </Text>
      </View>
    </View>
  );
};